import type { User } from '@toki/types';
import type { AuthProvider, AuthState, AuthToken } from './types.js';

const STORAGE_KEY_TOKEN = 'toki.auth.token';

interface TokiAuthConfig {
  /** Backend base URL, e.g. "http://localhost:8000" */
  baseUrl: string;
}

/** Traduce el primer error de validación Pydantic (FastAPI 422) a una
 *  frase humana en castellano. Devuelve null si no entiende el formato,
 *  para que el caller caiga al fallback genérico. */
function humanizeValidationDetail(detail: unknown[]): string | null {
  const first = detail[0];
  if (!first || typeof first !== 'object') return null;
  const err = first as {
    type?: string;
    loc?: unknown[];
    msg?: string;
    ctx?: Record<string, unknown>;
  };
  const fieldRaw = Array.isArray(err.loc) ? String(err.loc[err.loc.length - 1] ?? '') : '';
  const fieldLabels: Record<string, string> = {
    email: 'El email',
    password: 'La contraseña',
    display_name: 'El nombre',
    displayName: 'El nombre',
    name: 'El nombre',
  };
  const field = fieldLabels[fieldRaw] ?? 'El campo';
  switch (err.type) {
    case 'string_too_short': {
      const min = (err.ctx?.min_length as number | undefined) ?? 0;
      return `${field} debe tener al menos ${min} caracteres.`;
    }
    case 'string_too_long': {
      const max = (err.ctx?.max_length as number | undefined) ?? 0;
      return `${field} no puede tener más de ${max} caracteres.`;
    }
    case 'missing':
      return `${field} es obligatorio.`;
    case 'value_error':
    case 'string_pattern_mismatch':
    case 'string_email':
      if (fieldRaw === 'email') return 'El email no es válido.';
      return err.msg ? `${field}: ${err.msg}` : `${field} no es válido.`;
    default:
      return err.msg ? `${field}: ${err.msg}` : null;
  }
}

/**
 * Backend response from /auth/login, /auth/refresh and the portal's session.
 * CamelCase because the backend uses Pydantic alias_generator=to_camel.
 */
interface TokenResponse {
  accessToken: string;
  refreshToken: string;
  expiresAt: number;
  tokenType: string;
}

/**
 * Backend response from GET /users/me. Note the `is` prefix on booleans
 * which must be stripped when mapping to the frontend User type.
 */
interface UserMeResponse {
  id: string;
  email: string;
  displayName: string | null;
  avatarUrl: string | null;
  locale: string;
  role: string;
  isActive: boolean;
  isEmailVerified: boolean;
  phoneNumber: string | null;
  isPhoneVerified: boolean;
  isPadronVerified: boolean;
  padronRef: string | null;
  permissions: string[];
  areas: { id: string; name: string }[];
  createdAt: string;
}

function mapUser(raw: UserMeResponse): User {
  return {
    id: raw.id,
    email: raw.email,
    displayName: raw.displayName ?? raw.email,
    locale: raw.locale as User['locale'],
    role: raw.role,
    createdAt: raw.createdAt,
    emailVerified: raw.isEmailVerified,
    phoneNumber: raw.phoneNumber,
    phoneVerified: raw.isPhoneVerified,
    padronVerified: raw.isPadronVerified,
    padronRef: raw.padronRef,
    permissions: raw.permissions ?? [],
    areas: raw.areas ?? [],
  };
}

function mapToken(raw: TokenResponse): AuthToken {
  return {
    accessToken: raw.accessToken,
    refreshToken: raw.refreshToken,
    expiresAt: raw.expiresAt,
  };
}

function saveToken(token: AuthToken): void {
  try {
    localStorage.setItem(STORAGE_KEY_TOKEN, JSON.stringify(token));
  } catch {
    // localStorage unavailable — token lives in memory only this session.
  }
}

function loadToken(): AuthToken | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY_TOKEN);
    if (!raw) return null;
    return JSON.parse(raw) as AuthToken;
  } catch {
    return null;
  }
}

function clearToken(): void {
  try {
    localStorage.removeItem(STORAGE_KEY_TOKEN);
  } catch {
    // Ignore.
  }
}

/**
 * Real authentication provider that talks to the Toki FastAPI backend.
 * Uses fetch() directly (not ApiClient) to avoid circular dependencies.
 *
 * Session persistence: tokens are stored in localStorage. On initialize(),
 * the provider checks for a stored token, refreshes it if expired, and
 * fetches the user profile from /users/me.
 */
export class TokiAuthProvider implements AuthProvider {
  private readonly baseUrl: string;
  private readonly listeners = new Set<(state: AuthState) => void>();
  private state: AuthState = {
    status: 'loading',
    user: null,
    token: null,
    error: null,
  };
  private initialized = false;
  private refreshTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(config: TokiAuthConfig) {
    this.baseUrl = config.baseUrl;
  }

  /**
   * Schedule the next background refresh to fire ~1 minute before the access
   * token would expire. This keeps the session warm so ordinary API calls
   * never hit 401 mid-use. Fires once, reschedules itself after success.
   * Failures fall back to logout via getAccessToken(); the timer is then
   * cleared by the logout call.
   */
  private scheduleRefresh(): void {
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = null;
    }
    const token = this.state.token;
    if (!token) return;
    // Clamp to >= 5s so we never spin tightly if the token was already close
    // to expiry when we were called.
    const delay = Math.max(5_000, token.expiresAt - Date.now() - 60_000);
    this.refreshTimer = setTimeout(() => {
      void (async () => {
        await this.getAccessToken();
        this.scheduleRefresh();
      })();
    }, delay);
  }

  private clearRefreshTimer(): void {
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = null;
    }
  }

  getState(): AuthState {
    return this.state;
  }

  async initialize(): Promise<void> {
    if (this.initialized) return;
    this.initialized = true;

    const stored = loadToken();
    if (!stored) {
      this.setState({ status: 'guest', user: null, token: null, error: null });
      return;
    }

    // Token exists — try to restore session.
    try {
      let token = stored;
      // Refresh if access token is expired or about to expire (1 min buffer).
      if (Date.now() >= token.expiresAt - 60_000) {
        if (!token.refreshToken) {
          throw new Error('No refresh token');
        }
        token = await this.refreshToken(token.refreshToken);
      }
      const user = await this.fetchMe(token.accessToken);
      saveToken(token);
      this.setState({ status: 'authenticated', user, token, error: null });
      this.scheduleRefresh();
    } catch {
      clearToken();
      this.setState({ status: 'guest', user: null, token: null, error: null });
    }
  }

  subscribe(listener: (state: AuthState) => void): () => void {
    this.listeners.add(listener);
    listener(this.state);
    return () => {
      this.listeners.delete(listener);
    };
  }

  async logout(): Promise<void> {
    this.clearRefreshTimer();
    clearToken();
    this.setState({ status: 'guest', user: null, token: null, error: null });
  }

  async getAccessToken(): Promise<string | null> {
    const { token } = this.state;
    if (!token) return null;

    // Refresh if expired or about to expire.
    if (Date.now() >= token.expiresAt - 60_000 && token.refreshToken) {
      try {
        const newToken = await this.refreshToken(token.refreshToken);
        const user = this.state.user;
        saveToken(newToken);
        this.setState({ status: 'authenticated', user, token: newToken, error: null });
        return newToken.accessToken;
      } catch {
        await this.logout();
        return null;
      }
    }

    return token.accessToken;
  }

  // ── Optional login methods ──────────────────────────────────────────

  loginWithEmailPassword = async (email: string, password: string): Promise<void> => {
    const tokenResponse = await this.postAuth('/api/v1/auth/login', { email, password });
    const token = mapToken(tokenResponse);
    const user = await this.fetchMe(token.accessToken);
    saveToken(token);
    this.setState({ status: 'authenticated', user, token, error: null });
    this.scheduleRefresh();
  };

  /** Canjea el enlace mágico del portal y abre la sesión del cliente.
   *
   *  Misma sesión y mismo almacén que la de staff, a propósito: desde que la
   *  puerta es única (paso 37), lo que decide qué ve cada uno es el ROL que
   *  viene de `/auth/me`, no dos mecanismos de sesión paralelos.
   *
   *  ⚠️ No hay `refreshToken`. `/auth/portal/session` no lo emite: el token
   *  del portal ES la sesión y no hay contraseña detrás que la respalde. Al
   *  caducar, `initialize()` lo detecta, limpia y devuelve a la puerta — que
   *  es exactamente lo que tiene que pasar. */
  loginWithPortalLink = async (linkToken: string): Promise<void> => {
    const res = await this.postAuth('/api/v1/auth/portal/session', { token: linkToken });
    const token = mapToken(res);
    const user = await this.fetchMe(token.accessToken);
    saveToken(token);
    this.setState({ status: 'authenticated', user, token, error: null });
  };

  // `registerWithEmailPassword` se ha ido con su endpoint (K12, 2026-09-24):
  // `POST /api/v1/auth/register` está cerrado. Las cuentas de cliente las crea
  // Adaptia desde el panel y se entra al portal con un enlace al buzón, así que
  // no hay nada que registrar desde un cliente. Se borra en vez de dejarla
  // apuntando a una ruta muerta: un método que devuelve 404 es un bug esperando
  // a que alguien lo llame.

  // ── Internal helpers ────────────────────────────────────────────────

  private setState(next: AuthState): void {
    this.state = next;
    for (const listener of this.listeners) {
      listener(next);
    }
  }

  private async postAuth(path: string, body: unknown): Promise<TokenResponse> {
    const resp = await fetch(`${this.baseUrl}${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    if (!resp.ok) {
      const errorBody = await resp.json().catch(() => null);
      const detail = errorBody?.detail;
      // 1) Error de negocio { code, message } → mostramos message.
      // 2) Error de validación Pydantic → array de objetos; humanizamos.
      // 3) Detail string suelto → tal cual.
      // 4) Fallback al HTTP status.
      let message: string;
      if (detail && typeof detail === 'object' && !Array.isArray(detail) && typeof (detail as { message?: unknown }).message === 'string') {
        message = (detail as { message: string }).message;
      } else if (Array.isArray(detail)) {
        message = humanizeValidationDetail(detail) ?? `Datos inválidos (HTTP ${resp.status}).`;
      } else if (typeof detail === 'string') {
        message = detail;
      } else {
        message = `HTTP ${resp.status}`;
      }
      throw new Error(message);
    }
    return (await resp.json()) as TokenResponse;
  }

  private async refreshToken(refreshToken: string): Promise<AuthToken> {
    const resp = await this.postAuth('/api/v1/auth/refresh', {
      refresh_token: refreshToken,
    });
    return mapToken(resp);
  }

  /** Recarga `state.user` con los datos actuales de `/users/me`. Útil
   *  tras una acción que cambia el usuario en backend (p. ej. verificar
   *  email) — el state local lo refleja sin esperar a un re-login. */
  refreshUser = async (): Promise<void> => {
    if (this.state.status !== 'authenticated' || !this.state.token) return;
    try {
      const user = await this.fetchMe(this.state.token.accessToken);
      this.setState({ ...this.state, user });
    } catch {
      // Si /users/me falla, dejamos el estado como está; el próximo
      // refresh/login lo corregirá.
    }
  };

  private async fetchMe(accessToken: string): Promise<User> {
    const resp = await fetch(`${this.baseUrl}/api/v1/users/me`, {
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    if (!resp.ok) {
      throw new Error(`/users/me failed: ${resp.status}`);
    }
    const raw = (await resp.json()) as UserMeResponse;
    return mapUser(raw);
  }
}
